import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { CircularProgress, Box } from '@mui/material';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import Groups from './pages/Groups';
import OUs from './pages/OUs';
import LDAP from './pages/LDAP';
import Schema from './pages/Schema';
import Diagnostics from './pages/Diagnostics';
import Agents from './pages/Agents';
import Installers from './pages/Installers';
import Integrations from './pages/Integrations';
import Guide from './pages/Guide';
import BulkOperations from './pages/BulkOperations';
import ResetPassword from './pages/ResetPassword';
import GPO from './pages/GPO';
import SSHConfig from './pages/SSHConfig';
import EmailConfig from './pages/EmailConfig';
import WebUIUsers from './pages/WebUIUsers';
import Layout from './components/Layout';
import SSHSetupWizard from './components/SSHSetupWizard';
import { sshAPI, appAuthAPI, agentsAPI } from './services/api';
import { loginSuccess } from './store/authSlice';

function PrivateRoute({ children }) {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const user = useSelector((state) => state.auth.user);
  const token = useSelector((state) => state.auth.token);
  const dispatch = useDispatch();
  const [sshConfigured, setSSHConfigured] = useState(null); // null = loading
  const [showSSHWizard, setShowSSHWizard] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      checkSSHStatus();
    }
  }, [isAuthenticated]);

  // Rehydrate the signed-in user after a page refresh.
  //
  // Only the TOKEN is persisted (localStorage); `auth.user` starts null on every
  // reload. Anything that gates on the user -- is_staff for admin-only controls,
  // for instance -- therefore silently behaved as if the operator were not an
  // admin until they logged in again. That is not a missing feature, it reads
  // as buttons that vanish at random.
  useEffect(() => {
    if (isAuthenticated && token && !user) {
      appAuthAPI
        .getCurrentUser()
        .then((res) => dispatch(loginSuccess({ user: res.data.user || res.data, token })))
        .catch(() => {
          // Leave it null: the api interceptor already redirects to /login on a
          // 401, and a transient failure must not log the operator out.
        });
    }
  }, [isAuthenticated, token, user, dispatch]);

  // Decide whether to block the whole app on the SSH setup wizard.
  //
  // It used to block unconditionally whenever SSH was unconfigured. That was
  // right when SSH was the ONLY way to reach a domain controller. It is wrong
  // now: agents are the transport, SSH is a fallback that ships disabled, and
  // an operator who had not configured SSH could not reach ANY page -- including
  // /agents, the one page where you enroll the DC that makes the app work. The
  // wizard for a switched-off transport was standing in front of the fix for it.
  //
  // So the wizard blocks only when SSH is actually the transport in use.
  const checkSSHStatus = async () => {
    try {
      const [sshRes, policyRes] = await Promise.allSettled([
        sshAPI.getStatus(),
        agentsAPI.getPolicy(),
      ]);

      const configured =
        sshRes.status === 'fulfilled' ? Boolean(sshRes.value.data.setup_completed) : false;
      const sshIsTheTransport =
        policyRes.status === 'fulfilled'
          ? Boolean(policyRes.value.data.ssh_fallback_active)
          : // Policy unreadable (older backend, or the request failed). Fall back
            // to the previous behaviour rather than silently dropping a setup
            // step the deployment may still depend on.
            true;

      setSSHConfigured(configured);
      setShowSSHWizard(sshIsTheTransport && !configured);
    } catch (error) {
      console.error('Failed to check transport status:', error);
      setSSHConfigured(false);
      setShowSSHWizard(true);
    }
  };

  const handleSSHSetupComplete = () => {
    setSSHConfigured(true);
    setShowSSHWizard(false);
  };

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Show loading spinner while checking SSH status
  if (sshConfigured === null) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Show SSH setup wizard if not configured
  if (showSSHWizard) {
    return (
      <SSHSetupWizard
        open={showSSHWizard}
        onComplete={handleSSHSetupComplete}
      />
    );
  }

  return children;
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={<Login />} />
        {/* PUBLIC on purpose: someone resetting a password cannot sign in first.
            The backend has always emailed /reset-password?token=... and the
            confirm endpoint has always worked -- this route was simply absent,
            so the emailed link fell through to the authenticated shell and
            bounced the visitor to /login. That is the "link does nothing" bug. */}
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route
          path="/"
          element={
            <PrivateRoute>
              <Layout />
            </PrivateRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="users" element={<Users />} />
          <Route path="groups" element={<Groups />} />
          <Route path="ous" element={<OUs />} />
          <Route path="gpo" element={<GPO />} />
          <Route path="ldap" element={<LDAP />} />
          <Route path="schema" element={<Schema />} />
          <Route path="diagnostics" element={<Diagnostics />} />
          <Route path="agents" element={<Agents />} />
          <Route path="installers" element={<Installers />} />
          <Route path="integrations" element={<Integrations />} />
          <Route path="bulk" element={<BulkOperations />} />
          <Route path="guide" element={<Guide />} />
          <Route path="email-config" element={<EmailConfig />} />
          <Route path="ssh-config" element={<SSHConfig />} />
          <Route path="webui-users" element={<WebUIUsers />} />
        </Route>
      </Routes>
    </Router>
  );
}

export default App;
